/**
 * Wave A · Mobile resilience pass.
 *
 * PageErrorState — standard page-level error UI for mobile.
 * Shows an icon, title, description, and a retry button with optional
 * technical detail in a collapsed <details> element.
 *
 * Tokens: text-ink, text-ink-muted, bg-surface, border-line, text-err, text-brand-500
 * Mobile: min-h-[44px] on all interactive targets, safe-area-aware padding.
 */

import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

function WifiOffIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <circle cx="12" cy="20" r="1" />
    </svg>
  );
}

function AlertCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

interface PageErrorStateProps {
  /** Title shown prominently. Defaults to "Não consegui carregar agora". */
  title?: string;
  /** Descriptive sentence. Defaults to "Verifica a internet e tenta de novo." */
  description?: string;
  /** Label for the retry button. Defaults to "Tentar novamente". */
  retryLabel?: string;
  /** Called when retry is clicked. */
  onRetry?: () => void;
  /** If provided, shown in a collapsed <details> block. */
  errorDetail?: string;
  /** Which icon variant to use. Default "offline" (WifiOff). */
  iconVariant?: 'offline' | 'error';
  /** Layout variant. 'page' (default) fills min-h-screen; 'inline' fits inside a sub-section. */
  variant?: 'page' | 'inline';
  className?: string;
}

export function PageErrorState({
  title,
  description,
  retryLabel,
  onRetry,
  errorDetail,
  iconVariant = 'offline',
  variant = 'page',
  className,
}: PageErrorStateProps) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const resolvedTitle = title ?? (isEn ? "Couldn't load right now" : 'Não consegui carregar agora');
  const resolvedDescription = description ?? (isEn ? 'Check your internet and try again.' : 'Verifica a internet e tenta de novo.');
  const resolvedRetryLabel = retryLabel ?? (isEn ? 'Try again' : 'Tentar novamente');
  const Icon = iconVariant === 'offline' ? WifiOffIcon : AlertCircleIcon;

  const containerClass =
    variant === 'inline'
      ? 'flex w-full items-center justify-center py-8'
      : 'flex min-h-screen flex-col items-center justify-center gap-6 bg-surface px-6 pb-[max(24px,env(safe-area-inset-bottom))] pt-[env(safe-area-inset-top)]';

  return (
    <div
      className={cn(
        containerClass,
        variant === 'inline' && 'flex-col gap-4',
        className,
      )}
    >
      {/* Icon */}
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-err/10">
        <Icon className="h-7 w-7 text-err" />
      </div>

      {/* Text block — role="alert" so screen readers announce the failure
          the moment this mounts (assertive live region by default). */}
      <div role="alert" className="max-w-[65ch] space-y-2 text-center">
        <p className="text-[17px] font-semibold leading-snug text-ink">{resolvedTitle}</p>
        <p className="text-[14px] leading-relaxed text-ink-muted">{resolvedDescription}</p>
      </div>

      {/* Retry button */}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'min-h-[44px] rounded-xl bg-brand-500 px-6 text-[14px] font-semibold text-white',
            'transition-colors duration-120 ease-out-soft',
            'active:scale-[0.97] active:transition-none',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
          )}
        >
          {resolvedRetryLabel}
        </button>
      )}

      {/* Technical details — collapsed by default */}
      {errorDetail && (
        <details className="w-full max-w-[65ch] rounded-xl border border-line bg-surface-2 text-[13px]">
          <summary className="cursor-pointer select-none px-4 py-3 font-medium text-ink-muted hover:text-ink">
            {isEn ? 'Show technical details' : 'Mostrar detalhes técnicos'}
          </summary>
          <pre className="overflow-x-auto whitespace-pre-wrap break-all px-4 pb-4 pt-1 font-mono text-[12px] text-ink-muted">
            {errorDetail}
          </pre>
        </details>
      )}
    </div>
  );
}
